// @ts-nocheck
import React, { createContext, useContext, useEffect, useState } from "react";
import Preloader from "/components/layout/Preloader";
import { useWindowSize } from "/utils/hooks/use-EventListener";
import { useSoundHook } from "/utils/hooks/useSoundHook";
import sound from "/sounds/funtico_sports_bgm.mp3";
import AuthContext from "./AuthProvider";

/**
 * @typedef {import('../types/animation').Animation} Animation
 */

const hardcodedTimerData = {
  start_date: {
    time: "2025-11-16T15:00:00.000000Z",
    title: "EVENT STARTS IN:",
  },
  end_date: {
    time: "2025-11-22T21:00:00.000000Z",
    title: "EVENT ENDS IN:",
  },
};

// Check if event has already ended
const isEventEnded =
  Date.now() >= new Date(hardcodedTimerData.end_date.time).getTime();

const defTime = {
  isEnded: isEventEnded,
  title: "EVENT ENDED",
  timer: {
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  },
};

const AnimationContext = createContext({
  pageLoading: false,
  setPageLoading: (boo) => {},
  isMobile: false,
  setIsMobile: (boo) => {},
  timerData: defTime,
  event: null,
  setTimerData: (data) => {},
  setEvent: (event) => {},
  setAllowPlay: (boo) => {},
});
const stored_key = "is_sound";
// Round numbers less than 10
const round = (num) => (num < 10 ? `0${num}` : num);

export function useAnimation() {
  return useContext(AnimationContext);
}

export function AnimationProvider({ children }) {
  const { auth } = useContext(AuthContext);
  const width = useWindowSize();
  const [pageLoading, setPageLoading] = useState(true);
  const [isAllowPlay, setAllowPlay] = useState(false);
  const [isMobile, setIsMobile] = useState(width < 1025);
  const [timerData, setTimerData] = useState(defTime);
  const [event, setEvent] = useState(null);
  const [isSoundOn, setIsSoundOn] = useState(false);
  const { play, stop } = useSoundHook(sound, { loop: true });

  useEffect(() => {
    isSoundOn ? play?.() : stop?.();
    return () => {};
  }, [isSoundOn]);

  useEffect(() => {
    const checkStorage = localStorage.getItem(stored_key);

    const unlock = async () => {
      setIsSoundOn(true);
      cleanup();
    };

    const cleanup = () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
    };

    if (checkStorage == "off") return;
    document.addEventListener("click", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });

    return () => {};
  }, []);

  useEffect(() => {
    const boo = width < 1025;
    setIsMobile(boo);
    return () => {};
  }, [width]);

  useEffect(() => {
    if (!isAllowPlay) return;

    const initAnimation = () => {
      try {
        const mm = gsap.matchMedia();
        const body = document.body;
        // const kiwi = body.querySelector('[data-kivi]') ;
        const onlySections = body.querySelectorAll("section");
        const footer = body.querySelector(".footer");

        const asideDots = body.querySelector(".aside-dots");

        const sections = [...onlySections, footer];
        const sectionParallax = body.querySelector("[data-parallaxes]");

        // const paralaxesWrapper = sectionParallax?.querySelectorAll(
        //   "[data-paralax-wrapper]"
        // );
        const paralaxes = sectionParallax?.querySelectorAll("[data-paralax]");
        let scrollObserve = null;
        let isScrolling = true;
        let activeIndex = 0;
        let isLoaded = false;

        const scrollToSection = (id, duration = 1) => {
          const section = sections[id];

          const dots = [...asideDots.children];
          dots[id] && dots.forEach((dot) => dot.classList.remove("active"));
          dots[id]?.classList.add("active");

          activeIndex = id;
          isScrolling = true;
          gsap.to(window, {
            duration: duration,
            scrollTo: { y: section },
            onStart: () => {
              isScrolling = true;
            },
            onComplete: () => {
              isLoaded && (isScrolling = false);
            },
          });
        };

        const createDots = () => {
          if (!asideDots || window.innerWidth < 1024) return;
          asideDots.innerHTML = "";
          const onlySections = body.querySelectorAll("section");
          onlySections.forEach((s, i) => {
            const dot = document.createElement("button");
            dot.className = `dot`;
            dot.addEventListener("click", () => {
              if (isScrolling || activeIndex === i) return;
              scrollToSection(i);
            });
            asideDots.appendChild(dot);
          });
        };

        const handleParallax = (e) => {
          if (!paralaxes?.length) return;

          const fixer = -0.005;

          const pageX = (e.pageX - window.innerWidth / 2) * fixer;
          const pageY = (e.pageY - window.innerHeight / 2) * fixer;

          [...paralaxes].forEach((item) => {
            const speed = Number(item?.dataset.coff);

            gsap.to(item, {
              x: pageX * speed,
              y: pageY * speed,
              duration: 1,
            });
          });
        };

        const iniiDesktopFun = () => {
          const pins = [];

          scrollObserve = Observer.create({
            type: "wheel,touch,pointer,scroll",
            ignore: "[data-prevent-observer]",

            wheelSpeed: 1,
            onDown: () => {
              if (isScrolling || activeIndex == sections.length - 1) return;
              scrollToSection(activeIndex + 1);
            },
            onUp: () => {
              if (isScrolling || activeIndex == 0) return;
              scrollToSection(activeIndex - 1);
            },
            tolerance: 100,
            preventDefault: true,
          });

          window.scrollObserve = scrollObserve;

          document.addEventListener("mousemove", handleParallax);
          createDots();

          return () => {
            scrollObserve.kill();
            // kiviScr.kill();
            pins.forEach((pin) => pin?.kill());
            window.scrollObserve = null;

            document.removeEventListener("mousemove", handleParallax);
          };
        };

        const onLoaded = () => {
          scrollToSection(0, 0.1);
          setTimeout(() => {
            isScrolling = false;
            isLoaded = true;
          }, 1500);
        };

        const resize = new ResizeObserver(() => {
          setTimeout(() => {
            ScrollTrigger.refresh();
          }, 100);
        });
        resize.observe(body);

        mm.add("(min-width: 1025px)", iniiDesktopFun);
        onLoaded();
        window.scrollToSection = scrollToSection;
        window.sections = sections;
        window.createDots = createDots;
        return () => {
          resize.disconnect();
        };
      } catch (error) {
        return () => {
          console.log(error);
        };
      }
    };

    const cb = initAnimation();

    return () => {
      cb();
    };
  }, [isAllowPlay]);

  // Init timer
  useEffect(() => {
    if (pageLoading || timerData.isEnded) return;
    const { start_date, end_date } = hardcodedTimerData;

    const startTarget = new Date(start_date.time).getTime();
    const endTarget = new Date(end_date.time).getTime();

    let interval = null;

    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

    const initTimer = (target, title, nextPhase = null, nextTitle = null) => {
      const now = Date.now();

      // different in ms
      const diff = Math.max(target - now, 0);
      const totalSeconds = Math.ceil(diff / 1000);

      const days = pad(Math.floor(totalSeconds / (24 * 3600)));
      const hours = pad(Math.floor((totalSeconds % (24 * 3600)) / 3600));
      const minutes = pad(Math.floor((totalSeconds % 3600) / 60));
      const seconds = pad(totalSeconds % 60);

      const isEnded = diff === 0;

      setTimerData({
        // isEnded,
        title,
        timer: { days, hours, minutes, seconds },
      });

      if (!isEnded) {
        const msToNextSecond = 1000 - (now % 1000); // sync with clock
        interval = setTimeout(
          () => initTimer(target, title, nextPhase, nextTitle),
          msToNextSecond
        );
      } else if (nextPhase) {
        // start next phase
        initTimer(nextPhase, nextTitle);
      } else {
        // end of the end
        // setTimerData({ ...defTime, isEnded: true });
        document.body.classList.add("reloading");
        setTimeout(() => {
          location.reload();
        }, 1000);
      }
    };

    if (Date.now() < startTarget) {
      // not started yet → countdown to start
      initTimer(startTarget, start_date.title, endTarget, end_date.title);
    } else if (Date.now() < endTarget) {
      // already started → countdown to end
      initTimer(endTarget, end_date.title, false);
    } else {
      setTimerData({ ...defTime, isEnded: true });
    }

    return () => clearTimeout(interval);
  }, [pageLoading]);

  useEffect(() => {
    if (pageLoading) return;
    const emlement = document.querySelector("[data-kivi") ?? document;

    const scrollToTop = () => {
      window.scrollTo(0, 0);
      if (window.scrollY > 10) {
        scrollToTop();
      }
    };

    const onDocClick = (e) => {
      if (e !== null) {
        const target = e?.target;
        if (target.closest("header, .pop_up")) {
          return;
        }
      }

      document.body.classList.remove("not-played");
      document.removeEventListener("scroll", scrollToTop);
      emlement.removeEventListener("click", onDocClick);
      setAllowPlay(true);
    };

    setTimeout(() => {
      emlement.addEventListener("click", onDocClick);
    }, 1000);
    setTimeout(() => {
      document.body.classList.add("loaded");
      scrollToTop();
      document.addEventListener("scroll", scrollToTop);
    }, 0);
    return () => {};
  }, [pageLoading]);

  return (
    <AnimationContext.Provider
      value={{
        pageLoading,
        setPageLoading,
        isMobile,
        timerData,
        setTimerData,
        setEvent,
        setAllowPlay,
      }}
    >
      <>
        {children}
        <Preloader isLoading={pageLoading} />
        <button
          className={`sound-toggle ${isSoundOn ? "on" : "off"} ${auth?.token ? "logged-in" : ""}`}
          title={isSoundOn ? "Sound On" : "Sound Off"}
          onClick={() => {
            const type = !isSoundOn;
            !type
              ? localStorage.setItem(stored_key, "off")
              : localStorage.removeItem(stored_key);
            setIsSoundOn(type);
          }}
        ></button>
        <div className="aside-dots"></div>
      </>
    </AnimationContext.Provider>
  );
}
